<p-dialog
  [header]="title"
  [(visible)]="visible"
  [modal]="true"
  [responsive]="true"
  [style]="{width: '800px', minWidth: '300px'}"
  [closable]="!isSubmitting"
  (onHide)="closeDialog()">

  <!-- Scoped toast for entity form validation messages -->
  <p-toast key="entityFormToast" position="top-center"></p-toast>

  @if (isLoading) {
    <div class="loading-container text-center p-4">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
      <p>Loading data...</p>
    </div>
  }

  @if (!isLoading) {
    <form [formGroup]="entityForm" class="entity-form">
      <!-- Row 1: Entity Name (Full Width) -->
      <div class="row">
        <div class="col-md-12">
          <div class="form-group mb-3">
            <label for="name" class="form-label">
              Entity Name <span class="text-danger">*</span>
            </label>
            <input
              type="text"
              id="name"
              formControlName="name"
              class="form-control"
              [class.is-invalid]="entityForm.get('name')?.invalid && entityForm.get('name')?.touched"
              [readonly]="readOnly"
              placeholder="Enter entity name"
              maxlength="300">
            <div class="invalid-feedback">
              {{ getFieldErrorMessage('name') }}
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="groupAccountId" class="form-label">
              Group Account <span class="text-danger">*</span>
            </label>
            <!-- Read-only mode: show input with display name -->
            @if (readOnly) {
              <div [title]="entity?.groupAccountName || ''">
                <input
                  type="text"
                  class="form-control"
                  [value]="entity?.groupAccountName || '-'"
                  disabled>
              </div>
            }
            <!-- Edit mode: show dropdown -->
            @if (!readOnly) {
              <p-dropdown
                formControlName="groupAccountId"
                [options]="groupAccounts"
                optionLabel="name"
                optionValue="id"
                placeholder="Select group account"
                [style]="{'width': '100%'}"
                [class]="getDropdownClass('groupAccountId')"
                [filter]="true"
                filterBy="name"
                appendTo="body">
              </p-dropdown>
            }
            @if (entityForm.get('groupAccountId')?.invalid && entityForm.get('groupAccountId')?.touched) {
              <small class="text-danger">
                {{ getFieldErrorMessage('groupAccountId') }}
              </small>
            }
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="governingCountryId" class="form-label">
              Governing Country <span class="text-danger">*</span>
            </label>
            <!-- Read-only mode: show input with display name -->
            @if (readOnly) {
              <div [title]="entity?.governingCountryName || ''">
                <input
                  type="text"
                  class="form-control"
                  [value]="entity?.governingCountryName || '-'"
                  disabled>
              </div>
            }
            <!-- Edit mode: show dropdown -->
            @if (!readOnly) {
              <p-dropdown
                formControlName="governingCountryId"
                [options]="governingCountries"
                optionLabel="name"
                optionValue="id"
                placeholder="Select governing country"
                [style]="{'width': '100%'}"
                [class]="getDropdownClass('governingCountryId')"
                [filter]="true"
                filterBy="name"
                appendTo="body">
              </p-dropdown>
            }
            @if (entityForm.get('governingCountryId')?.invalid && entityForm.get('governingCountryId')?.touched) {
              <small class="text-danger">
                {{ getFieldErrorMessage('governingCountryId') }}
              </small>
            }
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="domesticJurisdictionId" class="form-label">
              Domestic Jurisdiction <span class="text-danger">*</span>
            </label>
            @if (readOnly) {
              <div [title]="entity?.domesticJurisdictionName || ''">
                <input
                  type="text"
                  class="form-control"
                  [value]="entity?.domesticJurisdictionName || '-'"
                  disabled>
              </div>
            }
            @if (!readOnly) {
              <p-dropdown
                formControlName="domesticJurisdictionId"
                [options]="jurisdictions"
                optionLabel="name"
                optionValue="id"
                placeholder="Select domestic jurisdiction"
                [style]="{'width': '100%'}"
                [class]="getDropdownClass('domesticJurisdictionId')"
                [filter]="true"
                filterBy="name"
                appendTo="body">
              </p-dropdown>
            }
            @if (entityForm.get('domesticJurisdictionId')?.invalid && entityForm.get('domesticJurisdictionId')?.touched) {
              <small class="text-danger">
                {{ getFieldErrorMessage('domesticJurisdictionId') }}
              </small>
            }
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="formationDate" class="form-label">Date of Formation</label>
            <input
              type="date"
              id="formationDate"
              formControlName="formationDate"
              class="form-control">
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="jurisdictionId" class="form-label">
              Jurisdiction <span class="text-danger">*</span>
            </label>
            @if (readOnly) {
              <div [title]="entity?.jurisdictionName || ''">
                <input
                  type="text"
                  class="form-control"
                  [value]="entity?.jurisdictionName || '-'"
                  disabled>
              </div>
            }
            @if (!readOnly) {
              <p-dropdown
                formControlName="jurisdictionId"
                [options]="jurisdictions"
                optionLabel="name"
                optionValue="id"
                placeholder="Select jurisdiction"
                [style]="{'width': '100%'}"
                [class]="getDropdownClass('jurisdictionId')"
                [filter]="true"
                filterBy="name"
                appendTo="body">
              </p-dropdown>
            }
            @if (entityForm.get('jurisdictionId')?.invalid && entityForm.get('jurisdictionId')?.touched) {
              <small class="text-danger">
                {{ getFieldErrorMessage('jurisdictionId') }}
              </small>
            }
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="authorizationDate" class="form-label">Date of Authorization</label>
            <input
              type="date"
              id="authorizationDate"
              formControlName="authorizationDate"
              class="form-control">
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="entityTypeId" class="form-label">
              Entity Type <span class="text-danger">*</span>
            </label>
            @if (readOnly) {
              <div [title]="entity?.entityTypeName || ''">
                <input
                  type="text"
                  class="form-control"
                  [value]="entity?.entityTypeName || '-'"
                  disabled>
              </div>
            }
            @if (!readOnly) {
              <p-dropdown
                formControlName="entityTypeId"
                [options]="entityTypes"
                optionLabel="name"
                optionValue="id"
                placeholder="Select entity type"
                [style]="{'width': '100%'}"
                [class]="getDropdownClass('entityTypeId')"
                [filter]="true"
                filterBy="name"
                appendTo="body">
              </p-dropdown>
            }
            @if (entityForm.get('entityTypeId')?.invalid && entityForm.get('entityTypeId')?.touched) {
              <small class="text-danger">
                {{ getFieldErrorMessage('entityTypeId') }}
              </small>
            }
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="jurisdictionIdNumber" class="form-label">Jurisdictional ID Number</label>
            <input
              type="text"
              id="jurisdictionIdNumber"
              formControlName="jurisdictionIdNumber"
              class="form-control"
              [class.is-invalid]="entityForm.get('jurisdictionIdNumber')?.invalid && entityForm.get('jurisdictionIdNumber')?.touched"
              placeholder="Enter jurisdictional ID number"
              maxlength="200">
            <div class="invalid-feedback">
              {{ getFieldErrorMessage('jurisdictionIdNumber') }}
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="registeredAgentId" class="form-label">
              Registered Agent <span class="text-danger">*</span>
            </label>
            @if (readOnly) {
              <div [title]="entity?.registeredAgentName || ''">
                <input
                  type="text"
                  class="form-control"
                  [value]="entity?.registeredAgentName || '-'"
                  disabled>
              </div>
            }
            @if (!readOnly) {
              <p-dropdown
                formControlName="registeredAgentId"
                [options]="registeredAgents"
                optionLabel="name"
                optionValue="id"
                placeholder="Select registered agent"
                [style]="{'width': '100%'}"
                [class]="getDropdownClass('registeredAgentId')"
                [filter]="true"
                filterBy="name"
                appendTo="body">
              </p-dropdown>
            }
            @if (entityForm.get('registeredAgentId')?.invalid && entityForm.get('registeredAgentId')?.touched) {
              <small class="text-danger">
                {{ getFieldErrorMessage('registeredAgentId') }}
              </small>
            }
          </div>
        </div>
        @if (title !== 'Add New Entity' && !isTaskContext) {
          <div class="col-md-6">
            <div class="form-group mb-3">
              <label for="subjectEntityId" class="form-label">Subject Entity ID</label>
              <input
                type="text"
                id="subjectEntityId"
                formControlName="subjectEntityId"
                class="form-control"
                [readonly]="readOnly"
                placeholder="Enter subject entity ID"
                maxlength="200">
            </div>
          </div>
        }
      </div>
    </form>
  }

  <ng-template pTemplate="footer">
    @if (!readOnly && !isEntityInfoComplete ||( basicEditMode && !readOnly)) {
      <div>
        <span appActionTooltip
          [pTooltip]="(isSubmitting || isFormInvalid()) ? getSaveDisabledReason() : getActionButtonLabel()"
          [actionDisabled]="isSubmitting || isFormInvalid()">
          <button
            type="button"
            class="btn btn-primary me-2"
            (click)="saveEntity()"
            [disabled]="isSubmitting || isFormInvalid()">
            @if (isSubmitting) {
              <i class="pi pi-spin pi-spinner"></i>
            }
            {{ isSubmitting ? 'Saving...' : getActionButtonLabel() }}
          </button>
        </span>
        <span appActionTooltip
          [pTooltip]="isSubmitting ? 'Cannot cancel while saving is in progress' : 'Close without saving'"
          [actionDisabled]="isSubmitting">
          <button
            type="button"
            class="btn btn-secondary"
            (click)="closeDialog()"
            [disabled]="isSubmitting">
            Cancel
          </button>
        </span>
      </div>
    }
  </ng-template>
</p-dialog>